import { HasProperty } from '@/lib/utils';

const string = options => ( ...args ) => {
	const str = args[0];

	const required = ( () => {
		if ( typeof options.required === 'function' ) {
			return options.required( ...args );
		}

		return options.required;
	} )();

	// single = because we want to match '0' and 0
	if ( required && !str && ( str != '0' ) ) { // eslint-disable-line
		return 'Required';
	}

	if ( options.pattern ) {
		const valid = options.pattern.test( str ) || ( !required && !str );

		if ( !valid ) {
			return 'Invalid';
		}
	}

	return true;

};

const object = validations => ( ...args ) => {
	const data = args[0];

	let allValid = true;

	const validationKeys = Object.keys( validations );
	const validation     = validationKeys.reduce( ( allValidation, key ) => {
		const item     = data[key];
		const validate = validations[key];

		const addedValidation = {};
		const itemTree        = args[1].shift( item );
		const validationInfo  = args[2];
		const childArgs       = [item, itemTree, validationInfo];

		const valid = validate( ...childArgs );

		addedValidation[key] = valid;
		allValid &= valid; // eslint-disable-line

		return Object.assign( allValidation, addedValidation );
	}, {} );

	return allValid || validation;
};

// ! this is the dumbest function
// ! ever written. it is perfectly
// ! equivalent to just calling func
// ! in the first place.
const custom = func => data => func( data );

const indexedObject = childrenValidation => ( obj, unit ) => {
	let allValid = true;

	const indexes = Object.keys( obj );
	const validation = indexes.reduce( ( validationObj, index ) => {
		const item = obj[index];

		const validations     = Object.keys( childrenValidation );
		const childValidation = validations.reduce( ( allChildValidation, key ) => {
			const validate       = childrenValidation[key];
			const childValidated = {};

			childValidated[key] = validate( item[key], unit, item );
			allValid &= childValidated[key]; // eslint-disable-line

			return Object.assign( allChildValidation, childValidated );
		}, {} );

		const addedValidation = {};
		addedValidation[index] = childValidation;

		return Object.assign( validationObj, addedValidation );
	}, {} );

	return allValid || validation;
};

const selection = ( value, required = true ) => {

	if ( ( value === null || value === '' || value === undefined ) && required ) {
		return 'Required';
	}

	const isArray = Array.isArray( value );

	// if it's an empty array also error out
	if ( isArray && value.length === 0 ) {
		return 'Required';
	}

	return true;
};

const selectOrNew = ( key, key2, required = true ) => ( ...args ) => {

	const value = args[0];

	if ( !required ) {

		if ( value[key] == null && value[key2] == null ) { // eslint-disable-line
			return true;
		}

	}

	if ( ( value[key] || value[key] == 0 ) && ( value[key2] === null ) ) { // eslint-disable-line
		return true;
	}

	if ( ( value[key2] || value[key2] == 0 ) && ( value[key] === null ) ) { // eslint-disable-line
		return true;
	}

	return false;

};

const array = ( options = {} ) => { // eslint-disable-line

	// this function is used as a couried
	// function around each of the methods
	// returned below to avoid repeated logic
	const applyOptions = additionalFunc => ( val, form, allValidation ) => {

		if ( !Array.isArray( val ) ) {
			throw new Error( `First parameter "val" must be array. Instead found ${val}` );
		}

		if ( HasProperty( options, 'minLength' ) ) {
			let { minLength } = options;

			if ( typeof minLength === 'function' ) {
				minLength = minLength( form, val );
			}

			if ( val.length < minLength ) {

				if ( minLength === 1 ) {
					return `Must add at least ${minLength} item`;
				}

				return `Must add at least ${minLength} items`;
			}
		}

		if ( options.noDuplicates ) {
			const createdItems = {};
			let hasDuplicates  = false;

			val.forEach( ( value ) => {
				const entry = value[options.noDuplicates];

				if ( createdItems[entry] ) {
					hasDuplicates = true;

					return;
				}

				createdItems[entry] = true;
			} );

			if ( hasDuplicates ) {
				return 'Remove duplicates';
			}
		}

		return additionalFunc( val, form, allValidation );
	};

	return {
		items : validator => applyOptions( ( val, form, allValidation ) => {

			let allValid = true;
			const validationObject = {};
			val.forEach( ( value, i ) => {
				const valid = validator( value, form, allValidation );

				// single = because reasons.
				// don't change it.
				if ( valid != true ) { // eslint-disable-line
					allValid = false;

					validationObject[i] = valid;
				}

			} );

			if ( allValid ) {
				return true;
			}

			return validationObject;
		} ),
	};
};

export const validate = ( data, fields ) => {
	let allValid = true;

	const fieldKeys  = Object.keys( fields );
	const validation = fieldKeys.reduce( ( allValidation, key ) => {
		// get the item to be tested and
		// the fields
		const item  = data[key];
		const field = fields[key];

		// define the arguments which will
		// be passed to the validation handlers
		const itemTree = [item, data];
		const args     = [item, itemTree, allValidation];

		const addedValidation = {};
		const valid = ( () => {

			// if the type is just a normal function, we can
			// just call it and return the value
			if ( typeof field.validate === 'function' ) {
				return field.validate( ...args );
			}

			// otherwise, it will have to be an array of
			// functions which we can loop through
			if ( !Array.isArray( field.validate ) ) {
				throw Error( 'field.validate must be a function or an array of functions' );
			}

			// try all the validations and find one that's
			// validated properly
			const validations  = field.validate.map( func => func( ...args ) );
			const validatedIdx = validations.findIndex( a => ( a === true || a == '1' ) ); // eslint-disable-line

			// if one of the validations has passed, we
			// can return true
			if ( validatedIdx !== -1 ) {
				return true;
			}

			// otherwise we're going to return the validation
			// with the fewest number of errors
			const leastWrong = validations.reduce( ( obj, thisValidation ) => {

				// on the first iteration, we need to default to whatever
				// validation we're looking at
				if ( !obj ) {
					return thisValidation;
				}

				if ( thisValidation === false && typeof obj === 'object' ) {
					return obj;
				}

				// otherwise, check how many errors are in this validation
				// compared to the previously leastWrong validation
				const theseErrorKeys = Object.keys( thisValidation );
				const thoseErrorKeys = Object.keys( obj );

				const theseErrors = theseErrorKeys.filter( a => thisValidation[a] !== true ); // true is not an error
				const thoseErrors = thoseErrorKeys.filter( a => obj[a] !== true ); // true is not an error

				// return this validation if it has fewer errors
				if ( theseErrors.length < thoseErrors.length ) {
					return thisValidation;
				}

				// return this validation if it has more of the properties
				// we're looking for
				const thisCommonPropCount = theseErrorKeys.filter( a => HasProperty( item, a ) ).length;
				const thatCommonPropCount = thoseErrorKeys.filter( a => HasProperty( item, a ) ).length;

				if ( thisCommonPropCount > thatCommonPropCount ) {
					return thisValidation;
				}

				// otherwise stick to the object we have
				return obj;
			}, null );

			return leastWrong;
		} )();

		addedValidation[key] = valid;
		allValid &= valid; // eslint-disable-line

		return Object.assign( allValidation, addedValidation );
	}, {} );

	return allValid || validation;
};

const number = ( options = {} ) => ( val ) => {

	if ( options.required && ( !val && val !== 0 ) ) {
		return 'Required';
	}

	if ( options.required === false && !val ) {
		return true;
	}

	// we have to use != instead of !== because
	// '10' != 10 evaluates to false
	// but
	// '10' !== 10 evaulates to true
	if ( typeof val !== 'number' && parseFloat( val, 10 ) != val ) { // eslint-disable-line
		return 'Must be a number';
	}

	return true;
};

const date = ( options = {} ) => ( val ) => {

	if ( options.required ) {

		if ( !val ) {
			return 'Required';
		}

		if ( typeof val === 'object' ) {

			const dateRangePattern = new RegExp( /.+-.+/g );

			const time = dateStamp => new Date( dateStamp ).getTime();

			const splitDate = val.split( '-' );

			const isInvalid = ( !time( splitDate[0] ) || !time( splitDate[1] ) );

			if ( isInvalid ) {
				return 'Invalid date';
			}

			if ( !dateRangePattern.test( val ) ) {
				return 'Date ranges have to be separated by a "-"';
			}

		}

		if ( typeof val === 'string' ) {

			return new Date( val ).getTime() ? true : 'Invalid date';

		}

	}

	return true;

};

export default {
	string,
	indexedObject,
	object,
	custom,
	selection,
	selectOrNew,
	number,
	date,
	array,
};
